import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { CredentialsService, I18nService, untilDestroyed } from '@app/core';
import {
  Document,
  DynamicDocumentData,
  DynamicTemplateThemeEnum,
  DynamicTemplateVariable,
  FieldDataToShow,
  FieldsValuesBySigner,
  FloatingField,
  InlineFieldContext,
  SecurityVerificationEnum,
  Signer,
  Whitelabel
} from '@app/models';
import { AppService, DocumentService, DynamicDocumentService, ErrorHandlerService, VerificationService, WhitelabelService } from '@app/services';
import { DocumentHistoryComponent } from './../documents/documents-show/document-history-sidebar/document-history-sidebar.component';
import { VerificationDataModalService } from '@app/pages/documents/shared/verification-data-modal/verification-data-modal.service';
import { DocumentGovbrValidateModalService } from '@app/pages/documents/documents-show/document-govbr-validate-modal/document-govbr-validate-modal.service';
import { DynamicDocumentViewerComponent } from '../documents/shared/dynamic-document-viewer/dynamic-document-viewer.component';
import { PdfViewerComponent } from '@app/shared/pdf-viewer/pdf-viewer.component';
import { of } from 'rxjs';

export enum MobileTab {
  Options = 'options',
  Signers = 'signers',
  Document = 'document',
  History = 'history'
}

@Component({
  selector: 'app-audit',
  templateUrl: './audit.component.html',
  styleUrls: ['./audit.component.scss'],
  standalone: false
})
export class AuditComponent implements OnInit {
  document: Document;
  floatingFields: FloatingField[];
  currentSigner: Signer;
  isLoading = false;
  showPdfViewer = false;
  showSigners = false;
  showHistory = false;
  activeTab: MobileTab = MobileTab.Options;
  isHistoryAlreadyOpened = false;
  sidebarContainer: 'signers' | 'history' = 'signers';
  verificationForm: UntypedFormGroup;
  isDocumentWhitelabelLoaded = false;
  isDocumentPage = false;
  @ViewChild('historyComponent', { static: false }) historyComponent: DocumentHistoryComponent;
  @ViewChild('historyComponentMobile', { static: false }) historyComponentMobile: DocumentHistoryComponent;
  @ViewChild('pdfViewer') pdfViewerRef?: PdfViewerComponent;
  readonly MobileTab = MobileTab;

  documentWhitelabel: Whitelabel = {
    main_color: null,
    logo: '',
    pdf_logo: '',
    pdf_logo_mini: ''
  };

  documentHTML: string;
  dynamicDocumentTheme: DynamicTemplateThemeEnum;
  alreadyFilledFields: FieldsValuesBySigner[];
  renderedDocumentScale = 1;
  templateVariables: DynamicTemplateVariable[];
  signersFieldsData: Record<string, FieldDataToShow[]>;

  readonly DynamicTemplateThemeEnum = DynamicTemplateThemeEnum;
  readonly InlineFieldContext = InlineFieldContext;

  get createdAtDateFormat(): string {
    if (this.i18nService.language === 'en-US') return 'MMMM d, y';
    return "d 'de' MMMM 'de' y";
  }

  get createdAtTimeFormat(): string {
    if (this.i18nService.language === 'en-US') return "'at' h:mm a";
    if (this.i18nService.language === 'es-MX') return "'a las' HH:mm";
    return "'às' HH:mm";
  }

  @ViewChild('renderedTemplate', { static: false }) renderedTemplate!: DynamicDocumentViewerComponent;

  constructor(
    public route: ActivatedRoute,
    public appService: AppService,
    public documentService: DocumentService,
    public whitelabelService: WhitelabelService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private verificationDataModalService: VerificationDataModalService,
    private documentGovbrValidateModalService: DocumentGovbrValidateModalService,
    private dynamicDocumentService: DynamicDocumentService,
    private changeDetectorRef: ChangeDetectorRef,
    private credentialsService: CredentialsService,
    private i18nService: I18nService,
    private verificationService: VerificationService
  ) {
    this.verificationForm = this.formBuilder.group({ code: '' });
  }

  ngOnInit() {
    if (this.route.snapshot.params.id && Object.keys(this.route.snapshot.queryParams || {}).includes('userValidation')) {
      this.verificationService.validateUserCreateDocument(this.route.snapshot.params.id).subscribe();
    } else if (this.route.snapshot.params.id) {
      this.isDocumentPage = true;
      this.documentService
        .documentWithFloatingFields({ id: this.route.snapshot.params.id })
        .pipe(
          switchMap(({ document, floatingFields }) => {
            if (document.is_dynamic_document) {
              this.getDynamicDocumentData(document.files.original).subscribe();
            }
            return of({ document, floatingFields });
          })
        )
        .subscribe({
          next: ({ document, floatingFields }) => {
            this.document = document;
            this.floatingFields = floatingFields;

            if (document.locale?.language && !this.credentialsService.isAuthenticated()) {
              this.i18nService.language = document.locale.language;
            }

            if (this.appService.isAutentiqueDomain()) {
              this.whitelabelService.loadWhitelabelByDocument({ documentId: this.document.id }).subscribe({
                next: whitelabel => {
                  this.documentWhitelabel = {
                    ...whitelabel,
                    pdf_logo: whitelabel.pdf_logo || this.whitelabelService.pdfLogo,
                    pdf_logo_mini: whitelabel.pdf_logo_mini || this.whitelabelService.pdfLogoMini
                  };

                  this.whitelabelService.setMetaTags({ iconUrl: whitelabel.pdf_logo_mini });
                  this.isDocumentWhitelabelLoaded = true;
                }
              });
            }

            if (this.route.snapshot.params.id.length < 49) {
              this.currentSigner = this.document.signatures.find(signer => signer.public_id === this.route.snapshot.params.id);
            }
            if (this.currentSigner && Object.keys(this.route.snapshot.queryParams || {}).includes('sv')) {
              this.openVerificationDataModal(this.currentSigner);
            }
            if (!this.documentService.isPastLifecycle(document) && !this.document.files?.original) {
              setTimeout(
                () =>
                  this.documentService
                    .retryDocumentFile({ id: this.route.snapshot.params.id })
                    .pipe(untilDestroyed(this))
                    .subscribe({
                      next: data => (this.document.files.original = data.files.original)
                    }),
                2000
              );
            }
          },
          error: error => {
            this.errorHandlerService.handle(error, { ignoreNotificationOn: ['document_not_found'] });
            this.router.navigate([this.appService.panelPath('/404')], { replaceUrl: true, state: { fromDocumentUrl: location.href } });
          }
        });

      if (!this.appService.isAutentiqueDomain()) {
        this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' }).subscribe({
          next: whitelabel => {
            this.isDocumentWhitelabelLoaded = true;
            this.documentWhitelabel = this.whitelabelService.buildWhitelabel(whitelabel);
          }
        });
      }
    }
  }

  isShowingExtra() {
    return this.showPdfViewer || this.showSigners || this.showHistory;
  }

  setActiveTab(tab: MobileTab) {
    const previousTab = this.activeTab;

    this.activeTab = tab;
    this.showSigners = tab === MobileTab.Signers;
    this.showPdfViewer = tab === MobileTab.Document;
    this.showHistory = tab === MobileTab.History;
    if (tab === MobileTab.Document && previousTab !== MobileTab.Document) {
      this.pdfViewerRef?.refreshFloatingFields();
    }
    if (tab === MobileTab.History) {
      this.changeSidebarContainer('history');
    }
  }

  goBack() {
    this.showPdfViewer = false;
    this.showSigners = false;
    this.showHistory = false;
    this.activeTab = MobileTab.Options;
  }

  changeSidebarContainer(container: 'signers' | 'history') {
    if (container === 'signers') this.sidebarContainer = 'signers';
    if (container === 'history' && this.document) {
      this.signersFieldsData = this.appService.getFieldsDataToShowOnHistory(this.document, this.templateVariables);

      if (this.historyComponent.documentData.id == null && this.showHistory === false) {
        this.historyComponent.documentData = {
          id: this.document.id,
          created_at: this.document.created_at,
          author: {
            name: this.document.author.name,
            email: this.document.author.email
          }
        };
        this.historyComponent.refreshHistory();
      }
      if (this.historyComponentMobile.documentData.id == null && this.showHistory === true) {
        this.historyComponentMobile.documentData = {
          id: this.document.id,
          created_at: this.document.created_at,
          author: {
            name: this.document.author.name,
            email: this.document.author.email
          }
        };
        this.historyComponentMobile.refreshHistory();
      }

      this.sidebarContainer = 'history';
      this.isHistoryAlreadyOpened = true;
    }
  }

  // magic numbers
  // 20 sempre vai ser o codigo para validar o cadastro de um usuario
  // se for entre 20 e 32 caracteres, é um código de assinatura por link e monta a URL de assinatura
  verify() {
    const code = this.verificationForm.get('code').value || '';
    this.verificationForm.markAllAsTouched();

    if (code?.length === 20) {
      this.verificationService.validateUserCreateDocument(code).subscribe();
      return;
    }

    if (code && this.documentService.isSignatureByLink(code)) {
      this.isLoading = false;
      this.router.navigate(['/assinar/', code], { queryParams: { signPrompt: 1 } });
      return;
    }

    this.isLoading = true;
    this.verificationService
      .signatureBySecurityVerificationCode({ code: code.toUpperCase().replace(/[^A-z0-9]+/g, '') })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: signatureId => this.router.navigate(['/assinar/', signatureId], { queryParams: { signPrompt: 1 } }),
        error: error => this.errorHandlerService.handleValidation(this.verificationForm, error)
      });
  }

  openGovbrValidateModal() {
    this.documentGovbrValidateModalService.open({ certificateUrl: this.document?.files?.pades }).subscribe();
  }

  hasQualifiedSignature(): boolean {
    return this.document?.qualified || this.document?.signatures?.some(s => s.certificate);
  }

  openVerificationDataModal(signer: Signer) {
    this.verificationDataModalService
      .open({ signer, hideManualApproval: true }, signer.verifications.find(verification => verification.type === SecurityVerificationEnum.Live) ? { size: 'lg' } : {})
      .subscribe();
  }

  setUppercase(control: AbstractControl) {
    control.setValue((control.value || '').toUpperCase().replace(/[^A-z0-9]+/g, ''));
  }

  getDynamicDocumentData(file: string) {
    return this.dynamicDocumentService.getDynamicDocumentData(file).pipe(
      tap((data: DynamicDocumentData) => {
        this.dynamicDocumentTheme = data.theme || DynamicTemplateThemeEnum.Modern;
        this.setDocumentHTML(data.content, data.variables);
      })
    );
  }

  setDocumentHTML(html: string, variables: DynamicTemplateVariable[]) {
    html = this.appService.cleanAttqDocumentMargins(html);
    this.documentHTML = html;
    this.templateVariables = variables;

    setTimeout(() => {
      this.alreadyFilledFields = this.appService.getAlreadyFilledFields(this.document, variables);
    });

    this.isLoading = false;
  }

  updateRenderedDocumentScale() {
    this.renderedDocumentScale = this.appService.scaleRenderedDocumentToFit(this.renderedTemplate?.editorRef.nativeElement, false, { widthFix: 384 + 309 });
    this.changeDetectorRef.detectChanges();
  }

  isWhitelabel() {
    return !this.appService.isAutentiqueDomain() && !!this.whitelabelService?.whitelabel?.domain;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateRenderedDocumentScale();
  }
}
